import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthContext";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";

import FaqListTab from "./tabs/FaqListTab";
import ReviewTab from "./tabs/ReviewTab";
import ImportTab from "./tabs/ImportTab";

const MAIN_TABS = {
  list: {
    label: "FAQ List",
    iconPath:
      "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  },
  review: {
    label: "Review Queue",
    iconPath:
      "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  import: {
    label: "Import",
    iconPath:
      "M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
};

const MainTabButton = ({ tabKey, config, activeTab, onTabChange }) => {
  const isActive = activeTab === tabKey;
  return (
    <button
      onClick={() => onTabChange(tabKey)}
      className={`w-full flex items-center justify-center gap-2 px-5 md:px-8 py-2 md:py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
        isActive
          ? "bg-orange-500 text-white"
          : "text-gray-600 hover:bg-slate-300"
      }`}
    >
      <svg
        className="w-5 h-5 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={config.iconPath}
        />
      </svg>
      <span>{config.label}</span>
    </button>
  );
};

function FaqDashboard() {
  const { currentUser } = useAuth();
  const location = useLocation();

  const [mainTab, setMainTab] = useState("list");

  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };
  const [language, setLanguage] = useState(getLangFromUrl());

  if (!currentUser) return null;

  return (
    <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg font-sans overflow-hidden">
      <Sidebar />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <div className="flex-1 flex flex-col overflow-hidden pt-12">
        <div className="flex-1 overflow-y-auto">
          <div className="w-5/6 mx-auto px-4 lg:px-8 py-8">
            {/* Page title */}
            <div className="relative mb-8 lg:mb-12 text-center">
              <h1 className="text-3xl lg:text-4xl font-bold text-black dark:text-white mb-2">
                FAQ Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                Manage FAQ entries, review AI-sourced answers, and import from
                Excel.
              </p>
            </div>

            {/* Tab navigation */}
            <div className="flex justify-center mb-8 w-full">
              <div className="w-full flex justify-between gap-1 sm:gap-2 p-1 sm:p-1.5 border border-gray-300/50 bg-gray-100 shadow-xl rounded-xl">
                {Object.entries(MAIN_TABS).map(([key, config]) => (
                  <MainTabButton
                    key={key}
                    tabKey={key}
                    config={config}
                    activeTab={mainTab}
                    onTabChange={setMainTab}
                  />
                ))}
              </div>
            </div>

            {/* Tab content */}
            <div className="animate-fadeIn">
              {mainTab === "list" && <FaqListTab />}
              {mainTab === "review" && <ReviewTab />}
              {mainTab === "import" && <ImportTab />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FaqDashboard;
